import '../css/app.css';

import { SettingsProvider } from '@/contexts/SettingsContext';
import { createInertiaApp } from '@inertiajs/react';
import { createRoot } from 'react-dom/client';
import './bootstrap';
import { initializeTheme } from './hooks/use-appearance';
import './lib/axios'; // Configure axios to suppress expected aborted request errors

const appName = import.meta.env.VITE_APP_NAME || 'Laravel';

const pageModules = import.meta.glob('./pages/**/*.tsx', { eager: true });
const normalizedPageModules: typeof pageModules = Object.fromEntries(
    Object.entries(pageModules).map(([key, mod]) => [key.toLowerCase(), mod]),
) as typeof pageModules;

createInertiaApp({
    title: (title) => `${title} - ${appName}`,
    resolve: (name) => {
        const pageKey = `./pages/${name}.tsx`;
        const page = pageModules[pageKey] ?? normalizedPageModules[pageKey.toLowerCase()];

        if (!page) {
            throw new Error(`Page not found: ${pageKey}`);
        }

        return page;
    },
    setup({ el, App, props }) {
        const root = createRoot(el);

        root.render(
            <SettingsProvider>
                <App {...props} />
            </SettingsProvider>,
        );
    },
    progress: {
        color: '#4B5563',
    },
});

// This will set light / dark mode on load...
initializeTheme();

// Suppress non-critical Google Maps CORS errors (gen_204 CSP test requests)
// These are internal Google Maps API requests that don't affect functionality
if (typeof window !== 'undefined') {
    const originalConsoleError = console.error;
    console.error = (...args: unknown[]) => {
        const message = args[0]?.toString() || '';
        // Suppress CORS errors for Google Maps gen_204 CSP test requests
        if (message.includes('Cross-Origin Request Blocked') && message.includes('gen_204') && message.includes('maps.googleapis.com')) {
            // Silently ignore - this is a non-critical Google Maps internal test request
            return;
        }
        originalConsoleError.apply(console, args);
    };

    // Also handle unhandled errors that might be logged
    window.addEventListener('error', (event) => {
        const message = event.message || '';
        const source = event.filename || '';
        if (message.includes('gen_204') || (source.includes('maps.googleapis.com') && message.includes('CORS'))) {
            event.preventDefault(); // Prevent default error logging
        }
    });
}
